/**
 * Workspaces (plan §13). Two traders, two ways of reading the same cockpit.
 *
 * WHAT THIS REPLACES: the workspace control used to call setTheme() and nothing else, and its
 * active state was inferred from the RESOLVED THEME. So picking Scott and then switching to dark
 * made the UI claim you were in Brucy's workspace — a control that lied about its own state, and
 * a "workspace" that was a theme button wearing a different label. A dead control on screen is its
 * own small untruth, and the both-workspace merge gate could not honestly be signed while it stood.
 *
 * WHAT A WORKSPACE ACTUALLY IS: a persisted choice of WHO IS READING, which carries a default
 * theme, an information density, and which panels start collapsed. Theme is downstream of it and
 * still independently overridable — a workspace stamps its theme on selection, and an explicit
 * theme choice afterwards wins until the workspace is switched again.
 *
 * A NOTE ON A SOURCE CONFLICT, resolved deliberately rather than silently: plan §13's prose says
 * Scott gets "tide/news collapsed", but the ratified mock keeps the tide (its code comment reads
 * "tide STAYS — it's Scott's primary read") and the HUD seat's own merge gate says "Scott
 * level-focused/light with tide kept". Two of three sources agree, and they are the two closest to
 * the screen. TIDE IS KEPT. Flagged to INTELCHAT rather than decided in silence.
 */

export type Workspace = "brucy" | "scott";

export interface WorkspaceConfig {
  id: Workspace;
  label: string;
  /** Stamped on selection. An explicit theme choice afterwards overrides it. */
  defaultTheme: "dark" | "light";
  /** Drives spacing and type scale through `data-density` on the root. */
  density: "dense" | "comfortable";
  /** Strikes either side of spot in the dealer ladder. */
  ladderRows: number;
  /** `grid-template-columns` for the cockpit split — Scott gives the level rail more room. */
  columns: string;
  /**
   * Panel ids that start collapsed. Ids for panels that do not exist yet are listed ON PURPOSE:
   * adding the tide or news panel should be a one-line registration, not a workspace redesign.
   */
  collapsed: readonly string[];
  /** Shown in the UI so the workspace explains itself instead of being a mystery toggle. */
  summary: string;
}

export const WORKSPACES: Record<Workspace, WorkspaceConfig> = {
  brucy: {
    id: "brucy",
    label: "BRUCY",
    defaultTheme: "dark",
    density: "dense",
    ladderRows: 33,
    columns: "2fr 1fr",
    collapsed: [],
    summary: "dark · dense · every panel",
  },
  scott: {
    id: "scott",
    label: "SCOTT",
    defaultTheme: "light",
    density: "comfortable",
    // Fewer, larger rows: he is reading levels, not scanning the whole chain.
    ladderRows: 21,
    // The level rail earns more width in a level-focused workspace.
    columns: "3fr 2fr",
    // Tide kept (see the header note). News and the flow tape start collapsed.
    collapsed: ["news", "flow"],
    summary: "light · level-focused · news + flow collapsed, tide kept",
  },
};

const STORAGE_KEY = "spx-hub.workspace";

export function storedWorkspace(): Workspace {
  if (typeof localStorage === "undefined") return "brucy";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "scott" || v === "brucy" ? v : "brucy";
}

/**
 * Persist the choice and stamp it on the root so CSS can respond without prop-drilling through
 * every panel. Returns the config so the caller can apply the parts CSS cannot express.
 */
export function applyWorkspace(id: Workspace): WorkspaceConfig {
  const cfg = WORKSPACES[id];
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-workspace", id);
    document.documentElement.setAttribute("data-density", cfg.density);
  }
  if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, id);
  return cfg;
}

/**
 * PER-USER PINNED PANEL PREFS — R717①'s missing clause, granted R753⑧ / re-affirmed R763⑦.
 *
 * ⛔ WHAT WAS ALREADY HERE AND WHAT WAS NOT. `collapsed` is a per-workspace DEFAULT and it worked;
 * what never existed is the user's own choice surviving a reload. A reader who expanded Scott's
 * news panel got it back collapsed on every refresh, because the fold lived in component `$state`
 * and nothing wrote it down. ⇒ *** A DEFAULT IS NOT A PREFERENCE: one is what the workspace
 * proposes, the other is what this reader decided, and only the second needs storage. ***
 *
 * ⚠ SCOPED PER WORKSPACE ON PURPOSE. Brucy's dense cockpit and Scott's level-focused one disagree
 * about which panels earn space; one flat map would let a choice made as Scott follow you into
 * Brucy and read as a bug in the workspace rather than a leak in the store.
 *
 * ⚠ EVERY READ IS DEFENSIVE, AND NOT AS A COURTESY: `localStorage` throws outright in some
 * embeddings, returns null in private windows, and can hold whatever a previous version wrote.
 * A prefs store that can throw on boot takes the whole cockpit with it — so a corrupt or absent
 * value must degrade to THE DEFAULT, never to an exception and never to "expanded".
 */
const PREFS_KEY = "spx-hub.panels.v1";

type PanelPrefs = Partial<Record<Workspace, Record<string, boolean>>>;

function readPrefs(): PanelPrefs {
  try {
    if (typeof localStorage === "undefined") return {};
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // A non-object (or an array, or null) is a value this version did not write. Discard it.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    return parsed as PanelPrefs;
  } catch {
    return {};
  }
}

/**
 * The reader's own choice for one panel, or `null` when they have never expressed one.
 * ⚠ `null` and `false` are DIFFERENT ANSWERS — "no preference" must fall through to the
 * workspace default, while "deliberately expanded" must override a default that says collapsed.
 * Returning a bare boolean here would collapse those two and silently discard the second.
 */
export function storedCollapsed(ws: Workspace, panelId: string): boolean | null {
  const v = readPrefs()[ws]?.[panelId];
  return typeof v === "boolean" ? v : null;
}

/** Persist one panel's fold state for one workspace. A write failure must never reach the UI. */
export function rememberCollapsed(ws: Workspace, panelId: string, collapsed: boolean): void {
  try {
    if (typeof localStorage === "undefined") return;
    const prefs = readPrefs();
    prefs[ws] = { ...(prefs[ws] ?? {}), [panelId]: collapsed };
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* a full or blocked store costs the preference, never the cockpit */
  }
}

/**
 * Does this panel start collapsed? THE READER'S CHOICE OUTRANKS THE WORKSPACE DEFAULT.
 * Falls back to `cfg.collapsed` whenever no choice has been recorded — so an untouched cockpit
 * behaves exactly as it did before this clause existed.
 */
export function isCollapsed(cfg: WorkspaceConfig, panelId: string): boolean {
  const chosen = storedCollapsed(cfg.id, panelId);
  return chosen ?? cfg.collapsed.includes(panelId);
}
